import { useState, useEffect, useRef } from 'react';
import api, { API } from '../lib/api';
import { toast } from 'sonner';
import { UtensilsCrossed, Copy, RefreshCw, Download, Upload, Save, Trash2, ExternalLink, Clock } from 'lucide-react';

// Pure standard-library Python helper (no pip install needed). Watches a local
// PDF file and uploads it on every change.
const UPLOADER_TEMPLATE = `#!/usr/bin/env python3
"""La Contadina - Tageskarte Live Uploader.

Beobachtet eine lokale PDF-Datei und lädt sie automatisch hoch, sobald sie sich
aendert. Nur Python-Standardbibliothek (Python 3.7+), keine Installation noetig.

Einrichtung:
  1. Speichern Sie Ihre Tageskarte als PDF unter PDF_PATH (siehe unten).
  2. Starten:  python3 lacontadina_tageskarte_uploader.py
  3. Laufen lassen. Jede Aenderung am PDF wird automatisch veroeffentlicht.
"""
import os, time, uuid, urllib.request

UPLOAD_URL = "{API_URL}/tageskarte/upload"
TOKEN = "{TOKEN}"
PDF_PATH = r"C:\\LaContadina\\Tageskarte\\tageskarte.pdf"
CHECK_SECONDS = 60

def build_multipart(file_bytes, filename):
    boundary = "----LaContadina" + uuid.uuid4().hex
    crlf = b"\\r\\n"
    body = b"--" + boundary.encode() + crlf
    body += ('Content-Disposition: form-data; name="file"; filename="%s"' % filename).encode() + crlf
    body += b"Content-Type: application/pdf" + crlf + crlf
    body += file_bytes + crlf
    body += b"--" + boundary.encode() + b"--" + crlf
    return body, boundary

def upload(path):
    with open(path, "rb") as f:
        data = f.read()
    if not data.startswith(b"%PDF-"):
        print("[WARN] Keine gueltige PDF-Datei - uebersprungen.")
        return False
    body, boundary = build_multipart(data, os.path.basename(path))
    req = urllib.request.Request(UPLOAD_URL, data=body, method="POST")
    req.add_header("Content-Type", "multipart/form-data; boundary=" + boundary)
    req.add_header("X-Tageskarte-Token", TOKEN)
    try:
        with urllib.request.urlopen(req, timeout=30) as resp:
            print("[OK] Tageskarte hochgeladen (HTTP %s)" % resp.status)
            return True
    except Exception as e:
        print("[FEHLER] Upload fehlgeschlagen:", e)
        return False

def main():
    print("La Contadina Tageskarte-Uploader gestartet.")
    print("Beobachte:", PDF_PATH)
    last = None
    while True:
        try:
            if os.path.exists(PDF_PATH):
                st = os.stat(PDF_PATH)
                sig = (st.st_mtime, st.st_size)
                if sig != last:
                    if upload(PDF_PATH):
                        last = sig
            else:
                print("[WARN] Datei nicht gefunden:", PDF_PATH)
        except Exception as e:
            print("[FEHLER]", e)
        time.sleep(CHECK_SECONDS)

if __name__ == "__main__":
    main()
`;

export default function TageskarteTab() {
  const [cfg, setCfg] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef(null);
  const uploadUrl = `${API}/tageskarte/upload`;

  const load = async () => {
    try {
      const r = await api.get('/tageskarte/config');
      setCfg(r.data);
    } catch {
      toast.error('Konfiguration konnte nicht geladen werden');
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  const setField = (k, v) => setCfg(c => ({ ...c, [k]: v }));

  const save = async () => {
    setSaving(true);
    try {
      await api.put('/tageskarte/config', {
        publish_time: cfg.publish_time || '',
        title: cfg.title || '',
        chef_notes: cfg.chef_notes || '',
        allergens: cfg.allergens || '',
        email_enabled: cfg.email_enabled,
      });
      toast.success('Gespeichert');
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Fehler beim Speichern');
    } finally {
      setSaving(false);
    }
  };

  const copy = (text, label) => {
    navigator.clipboard.writeText(text);
    toast.success(`${label} kopiert`);
  };

  const regenerate = async () => {
    try {
      const r = await api.post('/tageskarte/regenerate-token');
      setCfg(c => ({ ...c, token: r.data?.token || '' }));
      toast.success('Neues Token erzeugt – Hilfsprogramm bitte neu herunterladen');
    } catch {
      toast.error('Fehler beim Erzeugen');
    }
  };

  const downloadUploader = () => {
    const script = UPLOADER_TEMPLATE.replace('{API_URL}', API).replace('{TOKEN}', cfg.token);
    const blob = new Blob([script], { type: 'text/x-python' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'lacontadina_tageskarte_uploader.py';
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  const onUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.type !== 'application/pdf' && !file.name.toLowerCase().endsWith('.pdf')) {
      toast.error('Bitte eine PDF-Datei wählen');
      return;
    }
    setUploading(true);
    const fd = new FormData();
    fd.append('file', file);
    try {
      const r = await api.post('/tageskarte/upload-admin', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      toast.success(r.data?.scheduled ? 'Hochgeladen – wird zur geplanten Uhrzeit veröffentlicht' : 'Tageskarte veröffentlicht');
      load();
    } catch (e2) {
      toast.error(e2.response?.data?.detail || 'Upload fehlgeschlagen');
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = '';
    }
  };

  const removeCurrent = async () => {
    if (!window.confirm('Aktuelle Tageskarte wirklich entfernen? Die Seite zeigt dann „wird aktualisiert".')) return;
    try {
      await api.delete('/tageskarte/current');
      toast.success('Tageskarte entfernt');
      load();
    } catch {
      toast.error('Fehler beim Entfernen');
    }
  };

  if (loading || !cfg) {
    return <div className="text-sm text-[#5C5C59]" data-testid="tageskarte-tab">Lädt…</div>;
  }

  const fmt = (iso) => { try { return new Date(iso).toLocaleString('de-DE'); } catch { return iso; } };

  return (
    <div className="space-y-6 max-w-3xl" data-testid="tageskarte-tab">
      {/* Intro */}
      <div className="bg-white border border-[#E5E5E0] rounded-lg p-6 space-y-3">
        <div className="flex items-center gap-2">
          <UtensilsCrossed className="w-5 h-5 text-[#B45309]" />
          <h2 className="font-display text-xl text-[#1C1C19]">Tageskarte Live</h2>
        </div>
        <p className="text-sm text-[#5C5C59]">
          Ihre Gäste sehen über einen festen Link (in der Bestätigungs-E-Mail) immer die <b>aktuelle</b> Tageskarte.
          Sie erstellen die Karte wie gewohnt in Word, exportieren als PDF – das Hilfsprogramm lädt sie automatisch hoch.
          Es gibt kein Archiv: jede neue Karte ersetzt die vorherige.
        </p>
        <div className="flex flex-wrap items-center gap-2">
          <a
            href={cfg.public_url}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="tageskarte-public-link"
            className="inline-flex items-center gap-2 text-sm text-[#1C1C19] underline underline-offset-2 hover:text-[#B45309]"
          >
            <ExternalLink className="w-4 h-4" /> Öffentliche Seite ansehen
          </a>
          <span className="text-xs text-[#9CA3AF] font-mono">{cfg.public_url}</span>
        </div>

        {/* Status */}
        <div className="mt-2 flex flex-wrap gap-2 text-xs">
          {cfg.status?.has_current ? (
            <span className="px-2 py-1 rounded bg-[#F0FDF4] text-[#166534] border border-[#86EFAC]" data-testid="tageskarte-status-current">
              Aktiv seit {fmt(cfg.status.current_uploaded_at)}
            </span>
          ) : (
            <span className="px-2 py-1 rounded bg-[#FEF2F2] text-[#991B1B] border border-[#FECACA]" data-testid="tageskarte-status-none">
              Keine Tageskarte aktiv
            </span>
          )}
          {cfg.status?.has_pending && (
            <span className="px-2 py-1 rounded bg-[#FFFBEB] text-[#92400E] border border-[#FDE68A]" data-testid="tageskarte-status-pending">
              Geplant: Veröffentlichung um {fmt(cfg.status.pending_publish_at)}
            </span>
          )}
        </div>
      </div>

      {/* Manual upload + delete */}
      <div className="bg-white border border-[#E5E5E0] rounded-lg p-6 space-y-3">
        <h3 className="text-sm font-medium uppercase tracking-wider text-[#5C5C59]">Manuell hochladen</h3>
        <p className="text-xs text-[#5C5C59]">Alternativ zum Hilfsprogramm können Sie das PDF auch direkt hier hochladen.</p>
        <input ref={fileRef} type="file" accept="application/pdf" onChange={onUpload} data-testid="tageskarte-upload-input" className="hidden" />
        <div className="flex flex-wrap gap-2">
          <button
            onClick={() => fileRef.current?.click()}
            disabled={uploading}
            data-testid="tageskarte-upload-btn"
            className="inline-flex items-center gap-2 bg-[#1C1C19] text-white px-4 py-2.5 rounded text-sm hover:bg-[#3A3A35] transition disabled:opacity-50"
          >
            <Upload className="w-4 h-4" /> {uploading ? 'Lädt hoch…' : 'PDF hochladen'}
          </button>
          {cfg.status?.has_current && (
            <button
              onClick={removeCurrent}
              data-testid="tageskarte-delete-btn"
              className="inline-flex items-center gap-2 border border-[#FECACA] text-[#EF4444] px-4 py-2.5 rounded text-sm hover:bg-[#FEF2F2] transition"
            >
              <Trash2 className="w-4 h-4" /> Aktuelle entfernen
            </button>
          )}
        </div>
      </div>

      {/* Configuration */}
      <div className="bg-white border border-[#E5E5E0] rounded-lg p-6 space-y-4">
        <h3 className="text-sm font-medium uppercase tracking-wider text-[#5C5C59]">Einstellungen</h3>

        <div>
          <label className="text-xs text-[#5C5C59]">Titel der Seite</label>
          <input
            value={cfg.title || ''}
            onChange={e => setField('title', e.target.value)}
            data-testid="tageskarte-title-input"
            className="w-full mt-1 px-3 py-2 border border-[#E5E5E0] rounded text-sm focus:outline-none focus:border-[#1C1C19]"
            placeholder="Tageskarte"
          />
        </div>

        <div>
          <label className="text-xs text-[#5C5C59] flex items-center gap-1"><Clock className="w-3.5 h-3.5" /> Geplante Veröffentlichung (optional, HH:MM)</label>
          <input
            value={cfg.publish_time || ''}
            onChange={e => setField('publish_time', e.target.value)}
            data-testid="tageskarte-publish-time"
            className="w-32 mt-1 px-3 py-2 border border-[#E5E5E0] rounded text-sm font-mono focus:outline-none focus:border-[#1C1C19]"
            placeholder="11:30"
          />
          <p className="text-[11px] text-[#9CA3AF] mt-1">Leer = sofort veröffentlichen. Mit Uhrzeit: vorher hochgeladene PDFs werden erst zu dieser Uhrzeit sichtbar; bis dahin bleibt die vorherige Karte.</p>
        </div>

        <div>
          <label className="text-xs text-[#5C5C59]">Hinweis des Küchenchefs (optional)</label>
          <textarea
            value={cfg.chef_notes || ''}
            onChange={e => setField('chef_notes', e.target.value)}
            data-testid="tageskarte-chef-notes"
            rows={3}
            className="w-full mt-1 px-3 py-2 border border-[#E5E5E0] rounded text-sm focus:outline-none focus:border-[#1C1C19]"
            placeholder="z. B. Heute frischer Wildfang aus der Region…"
          />
        </div>

        <div>
          <label className="text-xs text-[#5C5C59]">Allergene / Hinweise (optional)</label>
          <textarea
            value={cfg.allergens || ''}
            onChange={e => setField('allergens', e.target.value)}
            data-testid="tageskarte-allergens"
            rows={3}
            className="w-full mt-1 px-3 py-2 border border-[#E5E5E0] rounded text-sm focus:outline-none focus:border-[#1C1C19]"
            placeholder="z. B. Glutenfrei auf Anfrage. Allergene gemäß Aushang."
          />
        </div>

        <label className="flex items-center gap-2 text-sm text-[#1C1C19]">
          <input
            type="checkbox"
            checked={!!cfg.email_enabled}
            onChange={e => setField('email_enabled', e.target.checked)}
            data-testid="tageskarte-email-enabled"
            className="w-4 h-4"
          />
          „Tageskarte Live"-Button in Bestätigungs-E-Mails anzeigen
        </label>

        <button
          onClick={save}
          disabled={saving}
          data-testid="tageskarte-save"
          className="inline-flex items-center gap-2 bg-[#1C1C19] text-white px-4 py-2.5 rounded text-sm hover:bg-[#3A3A35] transition disabled:opacity-50"
        >
          <Save className="w-4 h-4" /> {saving ? 'Speichert…' : 'Speichern'}
        </button>
      </div>

      {/* Helper program */}
      <div className="bg-white border border-[#E5E5E0] rounded-lg p-6 space-y-4">
        <h3 className="text-sm font-medium uppercase tracking-wider text-[#5C5C59]">Automatischer Upload (Hilfsprogramm)</h3>
        <div className="bg-[#F0FDF4] border border-[#86EFAC] rounded-md p-4 text-sm text-[#166534] space-y-1">
          <p className="font-medium">So richten Sie es ein (einmalig):</p>
          <ol className="list-decimal list-inside space-y-1 text-[#15803D]">
            <li>Tageskarte in Word erstellen → als PDF exportieren.</li>
            <li>PDF immer unter <span className="font-mono">C:\LaContadina\Tageskarte\tageskarte.pdf</span> speichern.</li>
            <li>Hilfsprogramm unten herunterladen (Token bereits eingetragen) und starten: <span className="font-mono">python3 lacontadina_tageskarte_uploader.py</span></li>
            <li>Laufen lassen – jede Änderung am PDF wird automatisch (alle 60 s) hochgeladen.</li>
          </ol>
        </div>

        <div>
          <label className="text-xs text-[#5C5C59]">Upload-URL</label>
          <div className="flex items-center gap-2 mt-1">
            <input readOnly value={uploadUrl} data-testid="tageskarte-url" className="flex-1 font-mono text-xs px-3 py-2 border border-[#E5E5E0] rounded bg-[#F9F9F7]" />
            <button onClick={() => copy(uploadUrl, 'URL')} data-testid="tageskarte-copy-url" className="p-2 border border-[#E5E5E0] rounded hover:bg-[#F9F9F7]" title="Kopieren"><Copy className="w-4 h-4" /></button>
          </div>
        </div>

        <div>
          <label className="text-xs text-[#5C5C59]">Token (geheim)</label>
          <div className="flex items-center gap-2 mt-1">
            <input readOnly value={cfg.token || ''} data-testid="tageskarte-token" className="flex-1 font-mono text-xs px-3 py-2 border border-[#E5E5E0] rounded bg-[#F9F9F7]" />
            <button onClick={() => copy(cfg.token, 'Token')} data-testid="tageskarte-copy-token" className="p-2 border border-[#E5E5E0] rounded hover:bg-[#F9F9F7]" title="Kopieren"><Copy className="w-4 h-4" /></button>
            <button onClick={regenerate} data-testid="tageskarte-regenerate" className="p-2 border border-[#E5E5E0] rounded hover:bg-[#F9F9F7] text-[#EF4444]" title="Neues Token erzeugen"><RefreshCw className="w-4 h-4" /></button>
          </div>
        </div>

        <button
          onClick={downloadUploader}
          disabled={!cfg.token}
          data-testid="tageskarte-download-helper"
          className="inline-flex items-center gap-2 bg-[#1C1C19] text-white px-4 py-2.5 rounded text-sm hover:bg-[#3A3A35] transition disabled:opacity-50"
        >
          <Download className="w-4 h-4" /> Hilfsprogramm herunterladen (Token eingetragen)
        </button>
      </div>
    </div>
  );
}
